import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { onAuthStateChanged, onIdTokenChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, claimsService, getArtifactProjectId } from '../firebaseInit';
import {
  ensureSession,
  listenForSessionConflict,
  clearSession,
  getLocalSessionToken,
  updateSessionActivity,
} from '../utils/sessionManager';

const DEBUG_AUTH = process.env.REACT_APP_DEBUG_AUTH === '1' || process.env.NODE_ENV === 'development';

const debugLog = (message, ...args) => {
  if (DEBUG_AUTH) {
    console.log(`🔐 [AUTH-CONTEXT] ${message}`, ...args);
  }
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [claims, setClaims] = useState(null);
  const [status, setStatus] = useState('initializing'); // 'initializing' | 'signedOut' | 'signedIn'
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Admin team switching - allows admins to view other teams
  // Persist to localStorage so selection survives page refresh
  const [viewingTeamId, setViewingTeamIdInternal] = useState(() => {
    try {
      const stored = localStorage.getItem('admin_viewing_team_id');
      return stored || null;
    } catch {
      return null;
    }
  });
  const [viewingTeamName, setViewingTeamName] = useState(null);
  
  // Wrapper to persist viewingTeamId to localStorage
  const setViewingTeamId = useCallback((teamId) => {
    setViewingTeamIdInternal(teamId);
    try {
      if (teamId) {
        localStorage.setItem('admin_viewing_team_id', teamId);
      } else {
        localStorage.removeItem('admin_viewing_team_id');
      }
    } catch (error) {
      debugLog('Failed to persist viewingTeamId:', error);
    }
  }, []);

    
  // Phase 3: Prevent duplicate claims processing (especially important for Safari)
  const isProcessingClaims = useRef(false);
  const processingTimestamp = useRef(null);
    
  // Track session start for 12h continuity - only within a login session
  // IMPORTANT: Don't persist across logins to avoid stale timestamps
  const [sessionStart] = useState(() => {
    // Always create a fresh sessionStart on component mount
    // This ensures users get a fresh 12-hour window after each login
    const now = Date.now();
    debugLog('Created new session start:', new Date(now));
    return now;
  });

  debugLog('AuthProvider initialized with sessionStart:', new Date(sessionStart));

  const processClaims = useCallback(async (firebaseUser) => {
    if (!firebaseUser) {
      debugLog('processClaims: No user provided, clearing claims');
      setClaims(null);
      return;
    }

    // Phase 3: Prevent duplicate processing
    if (isProcessingClaims.current) {
      const timeSinceStart = Date.now() - processingTimestamp.current;
      debugLog(`⚠️ Claims already being processed (started ${timeSinceStart}ms ago), skipping duplicate call`);
      return;
    }

    isProcessingClaims.current = true;
    processingTimestamp.current = Date.now();

    try {
      debugLog(`🔄 [CLAIMS-START] Processing claims for user: ${firebaseUser.email} at ${processingTimestamp.current}`);
            
      // Try to get existing claims first
      const token = await firebaseUser.getIdTokenResult();
      debugLog('Got token result, checking for existing claims...', {
        hasRole: !!token.claims.role,
        hasTeamId: !!token.claims.teamId,
        claims: token.claims
      });
            
      if (token.claims.role && token.claims.teamId) {
        const elapsedTime = Date.now() - processingTimestamp.current;
        debugLog(`✅ [CLAIMS-COMPLETE] Found existing claims in ${elapsedTime}ms:`, token.claims);
        setClaims({
          role: token.claims.role,
          teamId: token.claims.teamId,
          teamName: token.claims.teamName,
          status: token.claims.status
        });
        return;
      }

      // If no claims, set them
      debugLog('🔧 Setting claims for new user');
      const result = await claimsService.setClaimsForUser(firebaseUser.email);
      debugLog('setClaimsForUser result:', result);
            
      if (result.success) {
        // Force token refresh
        debugLog('Forcing token refresh...');
        await firebaseUser.getIdToken(true);
        const newToken = await firebaseUser.getIdTokenResult();
        debugLog('New token after refresh:', newToken.claims);
                
        const elapsedTime = Date.now() - processingTimestamp.current;
        debugLog(`✅ [CLAIMS-COMPLETE] Claims set successfully in ${elapsedTime}ms:`, newToken.claims);
                
        setClaims({
          role: newToken.claims.role,
          teamId: newToken.claims.teamId,
          teamName: newToken.claims.teamName,
          status: newToken.claims.status
        });
      }
    } catch (error) {
      const elapsedTime = Date.now() - processingTimestamp.current;
      console.error(`❌ [CLAIMS-ERROR] Error processing claims after ${elapsedTime}ms:`, error);
      console.error('❌ [CLAIMS-ERROR] Stack trace:', error.stack);
      debugLog('Claims processing error details:', error);
      setError(error);
      // Set basic fallback to prevent blocking
      debugLog('Setting fallback basic claims');
      setClaims({ role: 'basic', teamId: null, teamName: null, status: 'active' });
    } finally {
      isProcessingClaims.current = false;
      const totalTime = Date.now() - processingTimestamp.current;
      debugLog(`🏁 [CLAIMS-FINISH] Claims processing completed in ${totalTime}ms`);
    }
  }, []);

  // Check for session expiry (12h limit)
  const checkSessionExpiry = useCallback(() => {
    const now = Date.now();
    const sessionDuration = now - sessionStart;
    const twelveHours = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
        
    if (sessionDuration > twelveHours) {
      debugLog('⏰ Session expired after 12h, requiring re-auth');
      return true;
    }
    return false;
  }, [sessionStart]);

  useEffect(() => {
    debugLog('🔧 Setting up auth state listeners...');
    debugLog('🔍 Firebase auth object:', auth);
    debugLog('🔍 Firebase auth current user:', auth.currentUser);
        
    // Safari-specific: Check auth state immediately
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari) {
      debugLog('🍎 Safari detected - checking auth state immediately');
      // Use setTimeout to avoid blocking the main thread
      setTimeout(async () => {
        debugLog('🍎 Safari: Checking current user:', auth.currentUser);
        if (auth.currentUser) {
          // Phase 3: Check if claims are already being processed by main handler
          if (isProcessingClaims.current) {
            debugLog('🍎 Safari: Main handler already processing claims, skipping Safari-specific processing');
            return;
          }
                    
          debugLog('🍎 Safari: Found existing user, processing immediately');
          setIsLoading(true);
          setUser(auth.currentUser);
          setStatus('signedIn');
          await processClaims(auth.currentUser);
          debugLog('🍎 Safari: Claims processed, setting isLoading to false');
          setIsLoading(false);
        } else {
          debugLog('🍎 Safari: No existing user found');
        }
      }, 100); // Small delay to let Firebase initialize
    }
        
    // Auth state change listener
    const authUnsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
            
      debugLog('🔐 Auth state change detected:', firebaseUser ? 'User signed in' : 'User signed out');
      
      // DIAGNOSTIC: Log detailed info when user becomes null
      if (!firebaseUser) {
        debugLog('🚨 AUTH STATE NULL - Diagnostic info:', {
          timestamp: new Date().toISOString(),
          authCurrentUser: auth.currentUser?.email || 'none',
          wasAuthenticated: !!auth.currentUser,
          navigatorOnline: navigator.onLine,
          documentVisibility: document.visibilityState
        });
        
        // Check if this might be a spurious callback
        if (auth.currentUser) {
          debugLog('⚠️ SPURIOUS CALLBACK: auth.currentUser still exists, ignoring null callback');
          return; // Don't process this - auth.currentUser still exists
        }
      }
      
      if (isSafari) {
        debugLog('🍎 Safari: onAuthStateChanged fired, user:', firebaseUser?.uid || 'null');
      }
            
      setIsLoading(true);
      setError(null);
            
      if (firebaseUser) {
        debugLog('👤 User signed in:', firebaseUser.email);
        if (isSafari) {
          debugLog('🍎 Safari: Processing user sign in...');
        }
                
        // Check session expiry
        if (checkSessionExpiry()) {
          debugLog('Session expired, signing out user');
          await auth.signOut();
          return;
        }
                
        setUser(firebaseUser);
        setStatus('signedIn');
        await processClaims(firebaseUser);
                
        // Check if user is deactivated or removed after processing claims
        const userClaims = (await firebaseUser.getIdTokenResult()).claims;
        debugLog('🔍 User claims after processing:', userClaims);
        if (userClaims.status === 'inactive') {
          debugLog('🚫 User is inactive, signing out');
          await auth.signOut();
          // Redirect to login with deactivation message
          window.location.href = '/?deactivated=true';
          return;
        } else if (userClaims.status === 'removed') {
          debugLog('🚫 User has been REMOVED from team, signing out');
          await auth.signOut();
          // Redirect to login with removal message
          window.location.href = '/?removed=true';
          return;
        } else {
          debugLog('✅ User status is active:', userClaims.status);
        }
      } else {
        debugLog('👤 User signed out');
        if (isSafari) {
          debugLog('🍎 Safari: User signed out, clearing state');
        }
        setUser(null);
        setClaims(null);
        setStatus('signedOut');
        // Clear persisted viewing team on logout (session isolation)
        setViewingTeamId(null);
        debugLog('Cleared user state on sign out');
      }
      
      // Note: Session token cleanup happens in the session enforcement effect below
            
      debugLog('🔐 Auth state change processing complete, isLoading:', false);
      if (isSafari) {
        debugLog('🍎 Safari: Setting isLoading to false');
      }
      setIsLoading(false);
    });

    // Token change listener for claims updates
    // Note: We only check firebaseUser (not user state) to avoid closure issues
    const tokenUnsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      debugLog('🔄 ID token changed for user:', firebaseUser?.email || 'none');
      
      // DIAGNOSTIC: Log when token becomes null unexpectedly
      if (!firebaseUser && auth.currentUser) {
        debugLog('⚠️ TOKEN ANOMALY: onIdTokenChanged received null but auth.currentUser exists!');
        debugLog('⚠️ auth.currentUser:', auth.currentUser?.email);
        return; // Don't process this spurious callback
      }
      
      if (!firebaseUser) {
        debugLog('🔍 Token null - checking auth state:', {
          authCurrentUser: auth.currentUser?.email || 'none',
          timestamp: new Date().toISOString()
        });
      }
      
      if (firebaseUser) {
        // Token refreshed - check for updated claims
        debugLog('Checking for updated claims in refreshed token');
        try {
          const token = await firebaseUser.getIdTokenResult();
          if (token.claims.role) {
            debugLog('Token has claims, updating:', token.claims);
                        
            // Check if user became inactive or was removed
            debugLog('🔍 Token claims on refresh:', token.claims);
            if (token.claims.status === 'inactive') {
              debugLog('🚫 User became inactive, signing out');
              await auth.signOut();
              window.location.href = '/?deactivated=true';
              return;
            } else if (token.claims.status === 'removed') {
              debugLog('🚫 User was REMOVED from team, signing out');
              await auth.signOut();
              window.location.href = '/?removed=true';
              return;
            } else {
              debugLog('✅ User status still active on token refresh:', token.claims.status);
            }
                        
            setClaims({
              role: token.claims.role,
              teamId: token.claims.teamId,
              teamName: token.claims.teamName,
              status: token.claims.status
            });
          }
        } catch (error) {
          debugLog('Error checking token claims:', error);
        }
      }
    });

    // Timeout fallback to prevent infinite loading (longer for Safari compatibility)
    // Use a ref-like approach to check loading state to avoid stale closure
    const timeout = setTimeout(() => {
      // Check auth.currentUser directly instead of relying on potentially stale isLoading
      if (!auth.currentUser) {
        debugLog('⏰ Auth timeout reached after 5s - no user found, forcing signed out state');
        setIsLoading(false);
        setStatus('signedOut');
      } else if (isSafari) {
        // Safari-specific: Force check auth state one more time
        debugLog('🍎 Safari: Final auth state check after timeout');
        debugLog('🍎 Safari: Found user after timeout, ensuring state is set');
        setUser(auth.currentUser);
        setStatus('signedIn');
        processClaims(auth.currentUser);
      }
    }, 5000);

        
    return () => {
      if (authUnsubscribe && typeof authUnsubscribe === 'function') {
        authUnsubscribe();
      }
      if (tokenUnsubscribe && typeof tokenUnsubscribe === 'function') {
        tokenUnsubscribe();
      }
      clearTimeout(timeout);
    };
    // CRITICAL: Do NOT include 'user' in dependencies - it would cause auth listeners to 
    // unsubscribe and re-subscribe on every user state change, creating race conditions
  }, [processClaims, checkSessionExpiry]);

  // Clear persisted viewing team if user is not an admin (security/session isolation)
  useEffect(() => {
    if (claims && claims.role !== 'admin' && viewingTeamId) {
      debugLog('Clearing viewingTeamId - user is not admin');
      setViewingTeamId(null);
    }
  }, [claims, viewingTeamId, setViewingTeamId]);


  // Load team name when admin views another team
  useEffect(() => {
    if (!viewingTeamId) {
      setViewingTeamName(null);
      return;
    }
    
    const loadTeamName = async () => {
      try {
        const appId = getArtifactProjectId();
        const teamRef = doc(db, `artifacts/${appId}/teams`, viewingTeamId);
        const teamDoc = await getDoc(teamRef);
        if (teamDoc.exists()) {
          setViewingTeamName(teamDoc.data().name || viewingTeamId);
          debugLog('Loaded viewing team name:', teamDoc.data().name);
        } else {
          setViewingTeamName(viewingTeamId);
          debugLog('Team document not found, using ID as name');
        }
      } catch (error) {
        console.error('Error loading team name:', error);
        setViewingTeamName(viewingTeamId);
      }
    };
    
    loadTeamName();
  }, [viewingTeamId]);

  // =========================================================================
  // Single-session enforcement: prevent the same account from being logged in
  // on multiple browsers/devices simultaneously.
  // =========================================================================
  const sessionConflictHandled = useRef(false);

  useEffect(() => {
    const uid = user?.uid;
    if (!uid) {
      // User signed out — clear session token from localStorage
      // (uid is stale from previous render, so we clean up via a stored ref)
      return;
    }

    // Reset conflict flag for this session
    sessionConflictHandled.current = false;

    debugLog('🔐 [SESSION-ENFORCE] Setting up session enforcement for uid:', uid);

    // Ensure there's a local session token (handles sign-ups, page refresh, etc.)
    // Login.js handles fresh sign-ins; this is the fallback for other auth flows.
    ensureSession(uid, user.email)
      .then(() => {
        // Stamp lastActiveAt immediately on every auth event (page load, refresh, token
        // renewal). force:true bypasses the 5-min throttle so presence is always
        // accurate on load even if analytics hasn't fired yet (e.g. admins without a team).
        // email enriches activeSessions/userPresence for cross-env session matching.
        updateSessionActivity(uid, user.email, { force: true });
      })
      .catch((e) => {
        // Log prominently — if this fails, session enforcement is broken
        console.error('🚨 [SESSION-ENFORCE] ensureSession FAILED:', e.code || e.message);
        console.error('🚨 [SESSION-ENFORCE] Single-session enforcement will NOT work. Check Firestore rules.');
      });

    // Set up the Firestore listener that detects when another device logs in
    const unsubscribe = listenForSessionConflict(
      uid,
      () => {
        // Prevent double-handling (React StrictMode, rapid snapshot changes)
        if (sessionConflictHandled.current) return;
        sessionConflictHandled.current = true;

        debugLog('🚨 [SESSION-ENFORCE] Forcing sign-out — another device logged in');

        // Clear local session state
        clearSession(uid);

        // Sign out and redirect with a user-friendly message
        auth.signOut().then(() => {
          window.location.href = '/?session_conflict=true';
        }).catch(() => {
          // Fallback: redirect even if signOut fails
          window.location.href = '/?session_conflict=true';
        });
      },
      (listenerError) => {
        // If the listener fails (e.g. permission-denied), session enforcement is broken
        console.error('🚨 [SESSION-ENFORCE] Listener FAILED — session enforcement is NOT active:', listenerError.code || listenerError.message);
      },
    );

    return () => {
      debugLog('🔐 [SESSION-ENFORCE] Cleaning up session listener');
      unsubscribe();
    };
    // Only re-run when the user's UID changes (login/logout), not on token refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // Computed: is admin viewing another team?
  const isViewingOtherTeam = claims?.role === 'admin' && viewingTeamId && viewingTeamId !== claims?.teamId;

  // Computed: effective team is viewingTeamId (for admins viewing another team) or user's own team
  // DEPRECATED: Use userTeam instead - it now automatically switches based on viewing state
  const effectiveTeamId = isViewingOtherTeam ? viewingTeamId : claims?.teamId;

  const value = useMemo(() => ({
    user,
    claims,
    status,
    isLoading,
    error,
    sessionStart,
    checkSessionExpiry,
    isAuthenticated: !!user,
    isAdmin: claims?.role === 'admin',
    isTeamLead: claims?.role === 'team_lead',
    // userTeam/userTeamName auto-switch when admin views another team
    userTeam: isViewingOtherTeam ? viewingTeamId : claims?.teamId,
    userTeamName: isViewingOtherTeam ? viewingTeamName : claims?.teamName,
    userRole: claims?.role || 'basic',
    // Real team info (admin's actual team, not the one being viewed)
    realTeamId: claims?.teamId,
    realTeamName: claims?.teamName,
    // Admin team switching
    isViewingOtherTeam,
    viewingTeamId,
    setViewingTeamId,
    // DEPRECATED: Use userTeam instead - kept for backward compatibility during refactor
    effectiveTeamId,
    // Include database and user info for backward compatibility
    db: db,
    userId: user?.uid || null,
    userEmail: user?.email || null,
    isAuthReady: !isLoading && status !== 'initializing'
  }), [user, claims, status, isLoading, error, sessionStart, checkSessionExpiry, isViewingOtherTeam, viewingTeamId, viewingTeamName, setViewingTeamId, effectiveTeamId]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
